import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// Material
import Typography from "@material-ui/core/Typography";
// Custom Hooks
import useDynamicHotelData from "../../../CustomHooks/useDynamicHotelData";
// Styles
import useStyles from "./SelectOrderType.style.js";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import DeliveryTime from "../DeliveryTime/DeliveryTime.js";
import DateSlotPicker from "../../Checkout/DateSlotPicker/index.js";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_TYPES } from "../../../utils/Constants.js";
import { generateDateSlots } from "../../../utils/utils.js";
import TimePicker from "../../Checkout/TimePicker/index.js";
import moment from "moment";
import { Info } from "@material-ui/icons";
import { clearTipAmount } from "../../../store/Cart/CartActions.js";

const SelectOrderType = ({
  value = null,
  onSelect = () => {},
  formValues,
  handleClose,
  onDateAndTimeSelect,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { hotel, isTodayHoliday } = useSelector((state) => state.hotel);
  const [selectedOderType, setOrderType] = useState(value);
  const [dateSlots, setDateSlots] = useState([]);
  const [dateSlotsError, setDateSlotsError] = useState(false);
  const [date, setDate] = useState(null);
  const [typeOfOrder, setTypeOfOrder] = useState("SCHEDULED");
  const [time, setTime] = useState(null);
  const { checkoutDetails } = useSelector((state) => state.cart);
  const { theme } = useSelector((state) => state?.theme);
  const [orderTimeType, setOrderTimeType] = useState(null);
  const hasMounted = useRef(false);
  const {
    getOrderTypes,
    getDeliveryIntervalSlots,
    getTodayDeliveryIntervalSlots,
    isAsapAvailable,
    isMultipleOrderType,
    getAsapTime,
  } = useDynamicHotelData();
  const [newChange, setNewChange] = useState(false);
  const [noAvailableSlots, setNoAvailableSlots] = useState(false);
  // state which return is shedulued time have value or not

  const isOnlineOrderAvailable = useMemo(() => {
    return Boolean(hotel?.online_order_active === 1);
  }, [hotel?.online_order_active]);

  const isScheduledAvailable = useMemo(() => {
    const todayHasScheduled =
      Array.isArray(getTodayDeliveryIntervalSlots) &&
      getTodayDeliveryIntervalSlots.some(
        (slot) => slot.typeOfOrder === "SCHEDULED"
      );

    const futureHasScheduled =
      Array.isArray(getDeliveryIntervalSlots) &&
      getDeliveryIntervalSlots.some((slot) => slot.typeOfOrder === "SCHEDULED");

    const hasDateSlots = Array.isArray(dateSlots) && dateSlots.length > 0;

    return todayHasScheduled || (futureHasScheduled && hasDateSlots);
  }, [getTodayDeliveryIntervalSlots, getDeliveryIntervalSlots, dateSlots]);

  // function for ordertype selection
  const onOrderTypeSelect = (fieldName, type) => {
    setOrderType(type);
    onSelect(fieldName, type);
    dispatch(clearTipAmount());
  };

  // Select first order type if their is multiple ordertypes
  useEffect(() => {
    if (
      !formValues?.orderType &&
      getOrderTypes?.length > 0 &&
      !hotel?.order_later
    ) {
      onOrderTypeSelect("orderType", getOrderTypes[0].type);
    }
  }, [formValues?.orderType, getOrderTypes]);

  // switch to next orderType if, default selected ordertype has no asap and sheduled time

  useEffect(() => {
    if (formValues?.orderType && getOrderTypes?.length > 0) {
      const currentOrderType = formValues.orderType;
      const nextOrderType = getOrderTypes.find(
        (typeObj) => typeObj.type !== currentOrderType
      );

      if (
        !isAsapAvailable &&
        !isScheduledAvailable &&
        nextOrderType &&
        isTodayHoliday
      ) {
        const timeoutId = setTimeout(() => {
          onOrderTypeSelect("orderType", nextOrderType.type);
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    formValues?.orderType,
    getOrderTypes,
    isAsapAvailable,
    isScheduledAvailable,
    isTodayHoliday,
  ]);
  // console.log("isTodayHoliday", isTodayHoliday);
  // useEffect(() => {
  //   if (formValues?.orderType && getOrderTypes?.length > 0) {
  //     const currentOrderType = formValues.orderType;

  //     // If both ASAP and SCHEDULED are unavailable
  //     if (!isAsapAvailable && !isScheduledAvailable) {
  //       setNoAvailableSlots(true);
  //     } else {
  //       // Reset it if slots become available
  //       setNoAvailableSlots(false);
  //     }
  //   }
  // }, [
  //   formValues?.orderType,
  //   getOrderTypes,
  //   isAsapAvailable,
  //   isScheduledAvailable,
  // ]);

  // Select ordertimetype by default
  useEffect(() => {
    if (!newChange) {
      if (!hasMounted.current && formValues?.orderType) {
        // Initial render: Set default order time based on ASAP availability and order type
        const initialOrderTimeType =
          isAsapAvailable &&
          !isTodayHoliday &&
          !hotel?.order_later &&
          hotel?.openingAvailable &&
          (formValues?.orderType === "DELIVERY" ||
            formValues?.orderType === "PICKUP")
            ? "ASAP"
            : "SCHEDULED";
        setOrderTimeType(initialOrderTimeType);
        handleOrderTime(initialOrderTimeType);

        hasMounted.current = true; // Mark initial render as done
      } else {
        // On subsequent renders: Ensure correct default order time
        const shouldAsapBeDefault =
          !isTodayHoliday &&
          isAsapAvailable &&
          !hotel?.order_later &&
          hotel?.openingAvailable &&
          (formValues?.orderType === "DELIVERY" ||
            formValues?.orderType === "PICKUP");

        handleOrderTime(shouldAsapBeDefault ? "ASAP" : "SCHEDULED");
        setOrderTimeType(shouldAsapBeDefault ? "ASAP" : "SCHEDULED");
      }
    }
  }, [
    isAsapAvailable,
    formValues,
    hotel?.openingAvailable,
    newChange,
    isTodayHoliday,
  ]);

  const generateDatePickerSlots = () => {
    let orderTypeValue = value;
    if (value === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }
    const dates = generateDateSlots(hotel, orderTypeValue) || [];
    setDateSlots(dates);
    if (!dates || !dates?.length) {
      setDateSlotsError(true);
    }
  };

  useEffect(() => {
    const timeString = `${date}${time ? ` ${time}` : ""}`;
    const dTime = moment(moment(timeString)).toISOString();
    onDateAndTimeSelect(typeOfOrder || "SCHEDULED", dTime);
  }, [date, time, typeOfOrder]);

  useEffect(() => {
    if (hotel || formValues?.orderType || value) {
      generateDatePickerSlots();
    }
  }, [hotel, formValues?.orderType, value]);

  const onClick = () => {
    setNewChange(true);
  };

  const getAvailableTimeSlotsForDate = (selectedDate) => {
    // Get the time slots for today or another date based on the selected date
    const availableSlots = moment(selectedDate).isSame(moment(), "date")
      ? getTodayDeliveryIntervalSlots
      : getDeliveryIntervalSlots;

    // Add condition to filter for "SCHEDULED" time slots if orderTimeType is "SCHEDULED"
    if (orderTimeType === "SCHEDULED") {
      return availableSlots.filter((slot) => slot.typeOfOrder === "SCHEDULED");
    }

    return availableSlots; // Return all available slots if not filtered
  };

  const onSelectDataAndTime = ({ value, typeOfOrder } = {}, type) => {
    if (!value) return;
    if (typeOfOrder) {
      setTypeOfOrder(typeOfOrder);
    }
    if (type === "date") {
      // Set the selected date
      setDate(value);

      // Automatically select the first available time slot for the new date
      const availableTimeSlots = getAvailableTimeSlotsForDate(value);
      if (availableTimeSlots && availableTimeSlots.length > 0) {
        const firstTimeSlot = availableTimeSlots[0].value; // Assuming time slot object has a 'value' property
        setTime(firstTimeSlot); // Set the first available time
      }
    }

    if (type === "time") {
      setTime(value); // Set time manually if time is selected
    }
  };
  // Function to get the available time slots for a given date

  const getLabel = (arry) => {
    const asapSlot = arry.find((item) => item.typeOfOrder === "ASAP");
    return asapSlot ? asapSlot.label : undefined;
  };

  const filteredOrderTime = (array) => {
    const result = array.find((arr) => arr?.typeOfOrder === "ASAP");

    return result;
  };

  const filteredSheduledTimes = (array) => {
    const result = array.find((arr) => arr?.typeOfOrder === "SCHEDULED");

    return result;
  };

  const handleOrderTime = (e) => {
    setOrderTimeType(e);
    if (e === "ASAP") {
      const filteredTime = filteredOrderTime(
        getTodayDeliveryIntervalSlots?.length > 0
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots
      );
      onSelectDataAndTime(filteredTime, "ASAP");
      onSelectDataAndTime(filteredTime, "time");
    } else if (e === "SCHEDULED") {
      const filteredTime = filteredSheduledTimes(
        getTodayDeliveryIntervalSlots?.length > 0 && !hotel?.order_later
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots
      );
      onSelectDataAndTime(filteredTime, "SCHEDULED");
      onSelectDataAndTime(filteredTime, "time");
    }
  };

  const getDeliveryType = useMemo(() => {
    return selectedOderType;
  }, [selectedOderType]);

  const getLastTime = (array) => {
    if (!array || array?.length === 0) {
      return undefined;
    }

    // console.log("array", array);

    const boundaryTime = moment("05:00 AM", "hh:mm A");
    const referenceTime = moment("11:58 PM", "hh:mm A");

    // Step 1: Check if any time slot is after 11:45 PM
    const hasAfter11_45PM = array.some((item) =>
      moment(item.value, "hh:mm A").isAfter(referenceTime)
    );

    // Step 2: If there's a time after 11:45 PM and hotel is allowing preorder, consider before 5:00 AM slots
    if (hasAfter11_45PM && hotel?.is_preorder > 0) {
      const before5am = array.filter((item) => {
        const itemTime = moment(item.value, "hh:mm A");
        return itemTime.hours() < 5;
      });

      if (before5am.length > 0) {
        const sortedBefore5am = [...before5am].sort(
          (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
        );
        return sortedBefore5am[sortedBefore5am.length - 1]?.label;
      }
    }

    // Step 3: Fallback to return the last slot based on sorted time values
    const sortedArray = [...array].sort(
      (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
    );

    return sortedArray[sortedArray.length - 1]?.label;
  };

  // const getLastTime = (array) => {
  //   if (!array || array?.length === 0) {
  //     return undefined;
  //   }
  //   console.log("array", array);
  //   // Define the 5:00 AM boundary
  //   const boundaryTime = moment("05:00 AM", "hh:mm A");

  //   // Filter times before 5:00 AM
  //   const before5am = array.filter((item) => {
  //     const itemTime = moment(item.value, "hh:mm A");

  //     // Adjust: if the time is between 12:00 AM and 4:59 AM, treat as next day
  //     if (itemTime.hours() < 5) {
  //       return true;
  //     }

  //     return false;
  //   });

  //   // Return the last time before 5:00 AM if available and preorder condition matches
  //   if (before5am.length > 0 && hotel?.is_preorder > 0) {
  //     // Sort before5am by time value
  //     const sortedBefore5am = [...before5am].sort(
  //       (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
  //     );
  //     return sortedBefore5am[sortedBefore5am.length - 1]?.label;
  //   }

  //   // Sort full array by value in case it's unordered
  //   const sortedArray = [...array].sort(
  //     (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
  //   );

  //   return sortedArray[sortedArray.length - 1]?.label;
  // };
  // const getLastTime = (array) => {
  //   console.log("arrayyyyyy", array);
  //   if (!array || array.length === 0) {
  //     return undefined;
  //   }

  //   const lastElement = array[array.length - 1];

  //   return lastElement?.label;
  // };

  return (
    <>
      {/* <div className={classes.headerDiv}>
        {theme?.header_logo ? (
          <div className={classes.headerLogoDiv}>
            <img
              src={`data:image/png;base64, ${theme?.header_logo}`}
              alt="Header Logo"
              className={classes.headerLogo}
            />
          </div>
        ) : (
          <Typography
            className={clsx(classes.title)}
            variant="h1"
            display="block"
          >
            {hotel ? hotel.hotelName : ""}
          </Typography>
        )}
      </div> */}
      {/* {isMultipleOrderType && ( */}
      <div className={classes.modalTitleSection}>
        <Typography
          className={classes.modalTitle}
          variant="h1"
          align="center"
          paragraph
        >
          {"Select Order Type"}
        </Typography>
        <Divider />
      </div>
      {/* )} */}
      {/* {isMultipleOrderType && ( */}
      <div id="checkout-orderTypeForm" className={clsx(classes.container)}>
        {getOrderTypes?.map((o, i) => (
          <div
            key={i}
            className={clsx(
              classes.btnNormal,
              o.type === selectedOderType && classes.item
              // o.active === false && classes.itemDisabled
            )}
            onClick={() => {
              onOrderTypeSelect("orderType", o.type);
              setNewChange(false);
            }}
          >
            <div className={classes.itemDiv}>
              <o.Icon
                className={clsx(
                  o.type === selectedOderType
                    ? classes.itemIcon
                    : classes.normalIcon
                )}
              />
            </div>
            <Typography
              // color="primary"
              className={clsx(
                o.type === selectedOderType
                  ? classes.itemText
                  : classes.normalText
              )}
              variant="caption"
            >
              {o.title}
            </Typography>
          </div>
        ))}
      </div>
      {/* )} */}
      <div className={classes.modalTitleSection} style={{ marginTop: "16px" }}>
        <Typography
          className={classes.modalTitle}
          variant="h1"
          align="center"
          paragraph
        >
          {"Order Time"}
        </Typography>
        <Divider />
      </div>
      {noAvailableSlots ? (
        <div className={classes.noSlotDiv}>
          <Typography className={classes.noSlotTxt}>
            No available slots for {formValues.orderType}
          </Typography>
        </div>
      ) : (
        <div className={classes.timeSelectDiv}>
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              value={orderTimeType}
              name="radio-buttons-group"
              onChange={(e) => handleOrderTime(e.target.value)}
              onClick={onClick}
            >
              <FormControlLabel
                value="ASAP"
                className={classes.radioLabel}
                control={
                  <Radio
                    className={classes.radioBtn}
                    classes={{ checked: classes.checked }}
                  />
                }
                label={
                  !isAsapAvailable ||
                  !hotel?.openingAvailable ||
                  !getLabel(
                    getTodayDeliveryIntervalSlots.length > 0
                      ? getTodayDeliveryIntervalSlots
                      : getDeliveryIntervalSlots
                  )
                    ? "ASAP : Unavailable"
                    : `ASAP : ${getDeliveryType} in ${getLabel(
                        getTodayDeliveryIntervalSlots.length > 0
                          ? getTodayDeliveryIntervalSlots
                          : getDeliveryIntervalSlots
                      )}`
                }
                checked={
                  orderTimeType === "ASAP" &&
                  !hotel?.order_later &&
                  isOnlineOrderAvailable &&
                  !isTodayHoliday
                }
                disabled={
                  !isAsapAvailable ||
                  hotel?.order_later ||
                  !isOnlineOrderAvailable ||
                  !hotel?.openingAvailable ||
                  !getLabel(
                    getTodayDeliveryIntervalSlots.length > 0
                      ? getTodayDeliveryIntervalSlots
                      : getDeliveryIntervalSlots
                  )
                }
              />
              <FormControlLabel
                value="SCHEDULED"
                className={classes.radioLabel}
                disabled={!isOnlineOrderAvailable}
                control={
                  <Radio
                    className={classes.radioBtn}
                    classes={{ checked: classes.checked }}
                  />
                }
                label="Schedule for later"
                checked={orderTimeType === "SCHEDULED" || hotel?.order_later}
                // onChange={(e) => handleOrderTime(e.target.value)}
              />
            </RadioGroup>
          </FormControl>
          <div
            id="checkout-orderTypeForm"
            className={clsx(
              orderTimeType === "SCHEDULED"
                ? classes.sheduledContainer
                : classes.hideContainer
            )}
          >
            <DateSlotPicker
              slots={dateSlots}
              className={`w-100 ${classes.dateTimeSelection}`}
              onChange={onSelectDataAndTime}
            />
            <TimePicker
              slots={
                moment(formValues.deliveryTime).isSame(moment(), "date") &&
                // filteredTimeSlots(
                getTodayDeliveryIntervalSlots.length > 0
                  ? getTodayDeliveryIntervalSlots
                  : getDeliveryIntervalSlots
                // )
              }
              shouldFilter={true}
              isFromCheckout={false}
              date={date}
              onClick={onClick}
              orderTimeType={orderTimeType}
              className={`w-100 ${classes.dateTimeSelection}`}
              onChange={onSelectDataAndTime}
            />
          </div>

          {isOnlineOrderAvailable &&
            formValues?.deliveryType === "ASAP" &&
            getDeliveryType &&
            isAsapAvailable &&
            hotel?.openingAvailable && (
              <div className={classes.infoDiv}>
                <Info className={classes.infoIcon} />

                {isTodayHoliday ? (
                  /* 🟥 Holiday case */
                  <Typography className={classes.infoText}>
                    <span style={{ fontWeight: 800 }}>{getDeliveryType}</span>{" "}
                    is <u>not available today</u>
                  </Typography>
                ) : (
                  /* 🟩 Normal case */
                  !!getLastTime(
                    getTodayDeliveryIntervalSlots.length > 0
                      ? getTodayDeliveryIntervalSlots
                      : getDeliveryIntervalSlots
                  ) && (
                    <Typography className={classes.infoText}>
                      <span style={{ fontWeight: 800 }}>{getDeliveryType}</span>{" "}
                      available until{" "}
                      <span style={{ fontWeight: 800 }}>
                        {getLastTime(
                          getDeliveryIntervalSlots /* evening/future slots */
                        )}
                      </span>
                    </Typography>
                  )
                )}
              </div>
            )}
          {/* {isOnlineOrderAvailable &&
            getLastTime(
              getTodayDeliveryIntervalSlots.length > 0
                ? getTodayDeliveryIntervalSlots
                : getDeliveryIntervalSlots
            ) &&
            formValues?.deliveryType === "ASAP" &&
            getDeliveryType &&
            isAsapAvailable &&
            hotel?.openingAvailable && (
              <div className={classes.infoDiv}>
                <Info className={classes.infoIcon} />
                <Typography className={classes.infoText}>
                  <span style={{ fontWeight: "800" }}>{getDeliveryType}</span>{" "}
                  available until{" "}
                  <span style={{ fontWeight: "800" }}>
                    {getLastTime(
                      // getTodayDeliveryIntervalSlots.length > 0
                      //   ? getTodayDeliveryIntervalSlots
                      // :
                      getDeliveryIntervalSlots
                    )}
                  </span>
                </Typography>
              </div>
            )} */}
        </div>
      )}

      {/* {orderTimeType === "shedulued" && ( */}
      {/* )} */}
      <div className={classes.btnDiv}>
        <Button
          disabled={!formValues?.orderType || noAvailableSlots}
          className={classes.continueBtn}
          onClick={handleClose}
        >
          Continue
        </Button>
      </div>
    </>
  );
};

export default SelectOrderType;
